import { useCallback } from 'react';

import { cn } from '@lyearn/style';
import { Radio, Stack, Typography } from '@lyearn/ui';

import { getBoxRadioButtonStyles } from '../styles';
import { RadioButtonAppearance, RadioButtonProps } from '../types';

const BoxRadioButton = <T = string,>({
  size,
  isSelected,
  onChange,
  option,
  name,
  radioButtonClassName,
}: RadioButtonProps<T>) => {
  const { id, label, Icon, description, disabled: isDisabled, className } = option;
  const appearance: RadioButtonAppearance = description ? 'double' : 'single';

  const styles = getBoxRadioButtonStyles({
    appearance,
    isDisabled,
    isSelected,
    size,
    className,
  });

  const onClick = useCallback(() => {
    if (isDisabled) return;
    onChange?.(id, option);
  }, [id, isDisabled, onChange, option]);

  return (
    <Stack
      alignItems="center"
      aria-checked={isSelected}
      className={cn(styles.main.className, radioButtonClassName)}
      direction="row"
      role="radio"
      onClick={onClick}>
      {Icon ? <Icon className={styles.icon.className} /> : null}
      <Stack className={styles.labelWrapper} direction="col" gapY={2}>
        <Typography color="text-text-primary" numberOfLines={1} variant={styles.label.variant}>
          {label}
        </Typography>
        {styles.description?.variant ? (
          <Typography
            color="text-text-secondary"
            numberOfLines={1}
            variant={styles.description.variant}>
            {description}
          </Typography>
        ) : null}
      </Stack>
      <Radio
        checked={isSelected}
        className={styles.radio.className}
        disabled={isDisabled}
        id={id as string}
        name={name}
        size={styles.radio.size}
        value={id}
      />
    </Stack>
  );
};

export default BoxRadioButton;
